import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Heart, Sparkles, MapPin, GraduationCap } from 'lucide-react';
import PageQuote from '@/components/PageQuote';
import { useHeroImage } from '@/hooks/useHeroImage';
import HeroImageEditor from '@/components/HeroImageEditor';
import EditableText from '@/components/EditableText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hardcoded headshot image
const HEADSHOT_URL = 'https://customer-assets.emergentagent.com/job_auto-course-gen-1/artifacts/adv7nrq6_image.png';

export default function About() {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const heroImg = useHeroImage('about', about?.image_1_url);

  useEffect(() => {
    loadAbout();
  }, []);

  const loadAbout = async () => {
    try {
      const response = await axios.get(`${API}/about`);
      setAbout(response.data);
    } catch (error) {
      console.error('Failed to load about:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center" data-testid="loading-state">
          <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="min-h-screen relative bg-gradient-to-b from-[#2C1810] via-[#3D2817] to-[#2C1810]" data-testid="about-page">
        
        {/* Hero Section with Background Image */}
        {heroImg && (
          <div className="relative h-[300px] overflow-hidden">
            <HeroImageEditor pageName="about" />
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${heroImg})`,
                filter: 'sepia(30%) saturate(110%) brightness(0.6)'
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#2C1810]/60 to-[#2C1810]"></div>
          </div>
        )}

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          
          {/* Main Bio Section */}
          <div className={`grid md:grid-cols-3 gap-8 mb-16 relative z-10 items-start ${about?.image_1_url ? '-mt-32' : ''}`}>
            
            {/* Left Column - Headshot & Info */}
            <div className="md:col-span-1">
              <div className="cassette-card p-6 text-center">
                <div className="mb-6">
                  <img 
                    src={HEADSHOT_URL} 
                    alt="AcuErnie Headshot" 
                    className="w-48 h-48 rounded-full mx-auto object-cover border-4 border-[#E8934D] shadow-2xl"
                  />
                </div>
                <h1 className="text-4xl retro-title text-[#2C1810] mb-3" data-testid="page-title">
                  <EditableText as="span" id="about.hero.name">AcuErnie</EditableText>
                </h1>
                <div className="space-y-3 text-[#2C1810]">
                  <div className="flex items-center justify-center gap-2">
                    <GraduationCap className="w-5 h-5 text-[#E85D4D]" />
                    <p className="font-bold"><EditableText as="span" id="about.hero.role">Doctoral Student & Senior Intern</EditableText></p>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <MapPin className="w-5 h-5 text-[#E85D4D]" />
                    <p className="font-semibold"><EditableText as="span" id="about.hero.location">Chicago, IL</EditableText></p>
                  </div>
                  <p className="text-sm italic mt-4 border-t-2 border-[#E8934D] pt-4">
                    <EditableText as="span" id="about.hero.specialty">Acupuncture Specialist</EditableText>
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Bio Content */}
            <div className="md:col-span-2 space-y-6">
              
              {about ? (
                <>
                  {/* Philosophy Section */}
                  <div className="admin-card">
                    <div className="flex items-center gap-3 mb-6 border-b-2 border-[#E8934D] pb-4">
                      <Heart className="w-8 h-8 text-[#E8934D]" />
                      <h2 className="text-3xl retro-title" data-testid="philosophy-heading">My Philosophy</h2>
                    </div>
                    <div className="text-[#F4E4C1] text-lg leading-relaxed whitespace-pre-wrap" data-testid="philosophy-content">
                      {about.philosophy}
                    </div>
                  </div>

                  {/* Approach Section */}
                  <div className="admin-card">
                    <div className="flex items-center gap-3 mb-6 border-b-2 border-[#E8934D] pb-4">
                      <Sparkles className="w-8 h-8 text-[#E8934D]" />
                      <h2 className="text-3xl retro-title" data-testid="approach-heading">My Approach</h2>
                    </div>
                    <div className="text-[#F4E4C1] text-lg leading-relaxed whitespace-pre-wrap" data-testid="approach-content">
                      {about.approach}
                    </div>
                  </div>

                  {/* Bottom Image Section */}
                  {about.image_2_url && (
                    <div className="relative h-[400px] rounded-lg overflow-hidden shadow-2xl">
                      <div 
                        className="absolute inset-0 bg-cover bg-center"
                        style={{
                          backgroundImage: `url(${about.image_2_url})`,
                          filter: 'sepia(25%) saturate(105%)'
                        }}
                      ></div>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#2C1810]/80 via-transparent to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <div className="cassette-card p-6 inline-block">
                          <p className="text-[#2C1810] text-lg font-bold italic">
                            <EditableText as="span" id="about.quote">{'"Combining ancient wisdom with modern understanding to provide comprehensive, holistic care."'}</EditableText>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center cassette-card p-12" data-testid="no-content">
                  <p className="text-[#3D2817] text-xl">Content coming soon...</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* Page Quote */}
      <PageQuote pageName="about" position={1} variant="compact" />
    </Layout>
  );
}